import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import accountingApi from '../../services/accountingApi';

function LedgerStatement() {
    const [accountGroups, setAccountGroups] = useState([]);
    const [selectedAccountGroup, setSelectedAccountGroup] = useState('');
    const [fromDate, setFromDate] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0]);
    const [toDate, setToDate] = useState(new Date().toISOString().split('T')[0]);
    const [ledgerData, setLedgerData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [reportGenerated, setReportGenerated] = useState(false);

    useEffect(() => {
        loadAccountGroups();
    }, []);

    const loadAccountGroups = async () => {
        try {
            const response = await accountingApi.getAccountGroups();
            if (response.success) {
                const groups = Array.isArray(response.data) ? response.data : (response.data?.account_groups || []);
                setAccountGroups(groups);
            }
        } catch (error) {
            console.error('Error loading account groups:', error);
        }
    };

    const handleGenerateReport = async () => {
        if (!selectedAccountGroup) {
            setError('Please select an account group');
            return;
        }

        try {
            setLoading(true);
            setError(null);
            const response = await accountingApi.getLedgerStatement({
                account_group_id: selectedAccountGroup,
                from_date: fromDate,
                to_date: toDate
            });

            if (response.success) {
                setLedgerData(response.data);
                setReportGenerated(true);
            } else {
                setError(response.message || 'Failed to load ledger statement');
            }
        } catch (err) {
            console.error('Error loading ledger statement:', err);
            setError('An error occurred while loading the ledger statement');
        } finally {
            setLoading(false);
        }
    };

    const selectedGroup = accountGroups.find(g => g.id.toString() === selectedAccountGroup);

    return (
        <div className="max-w-7xl mx-auto px-4 py-8">
            {/* Header */}
            <div className="flex justify-between items-start mb-6">
                <div>
                    <h1 className="text-3xl font-bold text-slate-800 mb-2">Ledger Statement</h1>
                    <p className="text-slate-600">Account-wise transaction history with running balances</p>
                </div>
                <Link
                    to="/accounting/reports"
                    className="px-4 py-2 bg-slate-600 hover:bg-slate-700 text-white rounded-lg transition-colors flex items-center gap-2"
                >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                    Back to Reports
                </Link>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6">
                <h3 className="text-lg font-bold text-slate-800 mb-4">Filters</h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Account Group</label>
                        <select
                            value={selectedAccountGroup}
                            onChange={(e) => setSelectedAccountGroup(e.target.value)}
                            className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                        >
                            <option value="">Select Account Group</option>
                            {accountGroups.map(group => (
                                <option key={group.id} value={group.id}>{group.name}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Start Date</label>
                        <input
                            type="date"
                            value={fromDate}
                            onChange={(e) => setFromDate(e.target.value)}
                            className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">End Date</label>
                        <input
                            type="date"
                            value={toDate}
                            onChange={(e) => setToDate(e.target.value)}
                            className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                        />
                    </div>
                    <div className="flex items-end">
                        <button
                            onClick={handleGenerateReport}
                            disabled={loading}
                            className="w-full px-6 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-medium rounded-lg transition-colors"
                        >
                            {loading ? 'Generating...' : 'Generate Report'}
                        </button>
                    </div>
                </div>
            </div>

            {error && (
                <div className="bg-rose-50 border-l-4 border-rose-500 p-4 rounded-md mb-6">
                    <p className="text-sm text-rose-700">{error}</p>
                </div>
            )}

            {/* Empty State */}
            {!reportGenerated && !loading && (
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-12 text-center">
                    <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                    </div>
                    <h3 className="text-lg font-bold text-slate-800 mb-2">Select an Account Group</h3>
                    <p className="text-slate-600">Choose an account group and date range to generate the ledger statement.</p>
                </div>
            )}

            {/* Report Data */}
            {reportGenerated && ledgerData && !loading && (
                <>
                    {/* Account Info */}
                    <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-6 mb-6">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <p className="text-xs text-indigo-600 font-bold uppercase tracking-wider mb-1">Account</p>
                                <p className="text-lg font-bold text-indigo-900">{selectedGroup?.name}</p>
                            </div>
                            <div>
                                <p className="text-xs text-indigo-600 font-bold uppercase tracking-wider mb-1">Opening Balance</p>
                                <p className="text-lg font-bold text-indigo-900">₹{(ledgerData.opening_balance || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                            </div>
                            <div>
                                <p className="text-xs text-indigo-600 font-bold uppercase tracking-wider mb-1">Closing Balance</p>
                                <p className="text-lg font-bold text-indigo-900">₹{(ledgerData.closing_balance || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                            </div>
                        </div>
                    </div>

                    {/* Transactions Table */}
                    <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-slate-50 border-b border-slate-200">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-bold text-slate-500 uppercase">Date</th>
                                        <th className="px-6 py-3 text-left text-xs font-bold text-slate-500 uppercase">Voucher #</th>
                                        <th className="px-6 py-3 text-left text-xs font-bold text-slate-500 uppercase">Narration</th>
                                        <th className="px-6 py-3 text-right text-xs font-bold text-slate-500 uppercase">Debit (₹)</th>
                                        <th className="px-6 py-3 text-right text-xs font-bold text-slate-500 uppercase">Credit (₹)</th>
                                        <th className="px-6 py-3 text-right text-xs font-bold text-slate-500 uppercase">Balance (₹)</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {/* Opening Balance Row */}
                                    <tr className="bg-slate-50">
                                        <td colSpan="5" className="px-6 py-3 text-sm font-bold text-slate-700">Opening Balance</td>
                                        <td className="px-6 py-3 text-sm font-bold text-right text-slate-900">₹{(ledgerData.opening_balance || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                                    </tr>

                                    {ledgerData.entries && ledgerData.entries.length > 0 ? (
                                        ledgerData.entries.map((entry, index) => {
                                            const runningBalance = (ledgerData.opening_balance || 0) +
                                                ledgerData.entries.slice(0, index + 1).reduce((sum, e) =>
                                                    sum + (parseFloat(e.debit_amount) - parseFloat(e.credit_amount)), 0);

                                            return (
                                                <tr key={entry.id} className="hover:bg-slate-50">
                                                    <td className="px-6 py-4 text-sm text-slate-600">{entry.voucher_date}</td>
                                                    <td className="px-6 py-4 text-sm font-medium text-slate-900">{entry.voucher_number}</td>
                                                    <td className="px-6 py-4 text-sm text-slate-600">{entry.narration || '-'}</td>
                                                    <td className="px-6 py-4 text-sm text-right font-medium text-slate-900">
                                                        {parseFloat(entry.debit_amount) > 0 ? `₹${parseFloat(entry.debit_amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : '-'}
                                                    </td>
                                                    <td className="px-6 py-4 text-sm text-right font-medium text-slate-900">
                                                        {parseFloat(entry.credit_amount) > 0 ? `₹${parseFloat(entry.credit_amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : '-'}
                                                    </td>
                                                    <td className="px-6 py-4 text-sm text-right font-bold text-slate-900">
                                                        ₹{runningBalance.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    ) : (
                                        <tr>
                                            <td colSpan="6" className="px-6 py-12 text-center text-slate-400 italic">No transactions in this period</td>
                                        </tr>
                                    )}

                                    {/* Closing Balance Row */}
                                    <tr className="bg-indigo-50 border-t-2 border-indigo-200">
                                        <td colSpan="5" className="px-6 py-3 text-sm font-bold text-indigo-900">Closing Balance</td>
                                        <td className="px-6 py-3 text-sm font-bold text-right text-indigo-900">₹{(ledgerData.closing_balance || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

export default LedgerStatement;
